import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, formatApiError, inr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSettings } from "../lib/settings";
import { Check, ShieldCheck, Loader2, Tag } from "lucide-react";
import { toast } from "sonner";
import { loadRazorpay } from "../lib/razorpay";

// Messaging our WhatsApp Business API number from the customer's own phone opens the
// 24h session window, so every automated update after this point reaches them as a
// normal (not template-locked) message.
function openWhatsAppAfterPayment(waNumber, serviceName, orderId) {
  const text = `Hi HouseDesignWala! I've just paid for ${serviceName} — Order #${orderId.slice(0, 8).toUpperCase()}. What's next?`;
  const url = `https://wa.me/${waNumber}?text=${encodeURIComponent(text)}`;
  const win = window.open(url, "_blank");
  if (!win) {
    toast.message("Message us on WhatsApp for live updates", {
      action: { label: "Open WhatsApp", onClick: () => window.open(url, "_blank") },
      duration: 10000,
    });
  }
}

export default function Checkout() {
  const { slug } = useParams();
  const { user } = useAuth();
  const settings = useSettings();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [coupon, setCoupon] = useState("");
  const [discount, setDiscount] = useState(0);
  const [discountInfo, setDiscountInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  // B5: referral-reward credit balance, redeemable here as a discount.
  const [creditBalance, setCreditBalance] = useState(0);
  const [useCredit, setUseCredit] = useState(false);

  useEffect(() => { api.get(`/services/${slug}`).then(r => setS(r.data)); }, [slug]);
  useEffect(() => {
    if (user?.role === "customer") {
      api.get("/me/credit-balance").then(r => setCreditBalance(r.data?.balance || 0)).catch(() => {});
    }
  }, [user]);

  if (!s) return <div className="py-20 text-center">Loading…</div>;

  const creditApplied = useCredit ? Math.min(creditBalance, Math.max(0, s.price - discount)) : 0;
  const total = s.price - discount - creditApplied;

  const applyCoupon = async () => {
    if (!coupon.trim()) return;
    try {
      const { data } = await api.post("/coupons/validate", { code: coupon, amount: s.price });
      setDiscount(data.discount_amount);
      setDiscountInfo(data);
      toast.success(`Coupon applied! ${data.discount_percent}% off`);
    } catch (e) {
      setDiscount(0); setDiscountInfo(null);
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const proceed = async () => {
    if (!user) {
      toast.message("Please log in or register to continue");
      nav("/login", { state: { from: `/checkout/${slug}` } });
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/orders/create", {
        service_slug: slug,
        coupon_code: discountInfo ? coupon : null,
        use_credit_amount: creditApplied > 0 ? creditApplied : null,
      });

      // MOCK fallback (no razorpay keys configured)
      if (data.mocked) {
        toast.message("Razorpay checkout opening (mocked)…");
        await new Promise(r => setTimeout(r, 1200));
        await api.post("/orders/payment-verify", { order_id: data.order_id });
        toast.success("Payment successful! Now submit your requirements.");
        openWhatsAppAfterPayment(settings.whatsapp_number, data.service.name, data.order_id);
        nav(`/dashboard/requirements/${data.order_id}`);
        return;
      }

      // LIVE Razorpay
      const Razorpay = await loadRazorpay();
      const rzp = new Razorpay({
        key: data.key_id,
        amount: Math.round(data.amount * 100),
        currency: data.currency || "INR",
        order_id: data.razorpay_order_id,
        name: "HouseDesignWala™",
        description: `${data.service.name} — ${data.service.subtitle}`,
        image: "https://housedesignwala.com/api/files/18b1b263-2c1d-4631-8cfa-3c743bdb0809",
        prefill: {
          name: data.user?.name || user.name,
          email: data.user?.email || user.email,
          contact: data.user?.phone || user.phone || "",
        },
        notes: { order_id: data.order_id },
        theme: { color: "#F7941D" },
        handler: async (resp) => {
          try {
            await api.post("/orders/payment-verify", {
              order_id: data.order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            toast.success("Payment successful! Now submit your requirements.");
            openWhatsAppAfterPayment(settings.whatsapp_number, data.service.name, data.order_id);
            nav(`/dashboard/requirements/${data.order_id}`);
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Payment verification failed");
          }
        },
        modal: {
          ondismiss: () => {
            setLoading(false);
            toast.message("Payment cancelled — your order is saved as pending");
          },
        },
      });
      rzp.on("payment.failed", (e) => {
        toast.error(`Payment failed: ${e.error?.description || "Try again"}`);
        setLoading(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Order failed");
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#FEF3E2]/40 min-h-[80vh]">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[#1a3c5e]">Checkout</h1>
        <p className="text-sm text-gray-600 mt-1">Review your order and complete payment.</p>

        <div className="grid lg:grid-cols-5 gap-6 mt-8">
          <div className="lg:col-span-3 space-y-5">
            <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6 shadow-sm">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded bg-[#FEF3E2] text-[#F7941D]">{s.slug.toUpperCase()}</span>
                  <h2 className="font-display text-2xl font-bold text-[#1a3c5e] mt-2">{s.name}</h2>
                  <p className="text-sm text-gray-600">{s.subtitle}</p>
                </div>
                <div className="text-right">
                  <div className="font-display text-3xl font-extrabold text-[#F7941D]">{inr(s.price)}</div>
                  {s.original_price > s.price && <div className="text-sm text-gray-400 line-through">{inr(s.original_price)}</div>}
                </div>
              </div>
              <ul className="mt-4 grid sm:grid-cols-2 gap-2">
                {s.includes_list.slice(0, 6).map((x, i) => (
                  <li key={i} className="flex gap-2 text-sm text-gray-700"><Check className="w-4 h-4 text-emerald-600 mt-0.5" />{x}</li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-4 h-4 text-[#F7941D]" />
                <h3 className="font-display font-bold text-[#1a3c5e]">Have a coupon?</h3>
              </div>
              <div className="flex gap-2">
                <input value={coupon} onChange={(e) => setCoupon(e.target.value.toUpperCase())} placeholder="Enter code (e.g. FIRST10)" data-testid="coupon-input" className="flex-1 border border-gray-300 rounded-xl px-4 py-2.5 uppercase focus:outline-none focus:ring-2 focus:ring-[#F7941D]" />
                <button onClick={applyCoupon} data-testid="apply-coupon" className="px-5 py-2.5 bg-[#1a3c5e] hover:bg-[#122a42] text-white font-semibold rounded-xl">Apply</button>
              </div>
              {discountInfo && (
                <div className="mt-3 text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
                  ✓ {discountInfo.discount_percent}% OFF applied — saving {inr(discount)}
                </div>
              )}
            </div>

            {creditBalance > 0 && (
              <div className="bg-white rounded-2xl border border-[#1a3c5e]/10 p-6 shadow-sm" data-testid="credit-balance-card">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input type="checkbox" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} data-testid="use-credit-toggle" className="w-4 h-4 accent-[#F7941D]" />
                  <span className="text-sm text-gray-700">
                    You have <span className="font-bold text-[#1a3c5e]">{inr(creditBalance)}</span> referral credit — apply it to this order?
                  </span>
                </label>
              </div>
            )}
          </div>

          <aside className="lg:col-span-2">
            <div className="bg-white rounded-2xl border-2 border-[#F7941D]/30 p-6 shadow-xl lg:sticky lg:top-24">
              <h3 className="font-display font-bold text-[#1a3c5e] mb-3">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-gray-600"><span>Package</span><span className="font-semibold text-[#1a3c5e]">{s.name}</span></div>
                <div className="flex justify-between text-gray-600"><span>Price</span><span>{inr(s.price)}</span></div>
                {discount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount</span><span>-{inr(discount)}</span></div>}
                <div className="my-2 border-t border-dashed border-[#1a3c5e]/15" />
                <div className="flex justify-between font-display text-lg font-bold text-[#1a3c5e]"><span>Total</span><span className="text-[#F7941D]">{inr(total)}</span></div>
              </div>
              <button onClick={proceed} disabled={loading} data-testid="proceed-pay-btn" className="w-full mt-5 py-3 bg-[#F7941D] hover:bg-[#e0861a] disabled:opacity-60 text-white font-bold rounded-xl shadow-md transition flex items-center justify-center gap-2">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Proceed to Pay
              </button>
              <div className="mt-3 text-xs text-gray-500 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Secure Razorpay payment</div>
              <p className="text-[10px] text-gray-400 mt-2">After payment you will submit project requirements. Your order kicks off when our designer is assigned.</p>
              {!user && (
                <Link to="/login" state={{ from: `/checkout/${slug}` }} className="block text-center text-xs text-[#F7941D] font-semibold mt-3 hover:underline">Login or register to continue</Link>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
